import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { copyCampaignAsync } from 'components/campaigns/requests';
import { OpButton } from 'op2mise-react-widgets';
import OpTextBox from 'op2mise-react-widgets/op-textbox/OpTextBox';
import Switch from 'shared/components/switch/Switch';
import styles from './CopyCampaignModal.module.css';

export default function CopyCampaignModal({
  defaultCampaignData,
  onHide,
  openCampaignCopiedModal,
  setCopiedCampaignName,
}) {
  const [campaignData, setCampaignData] = useState({
    name: defaultCampaignData.name,
    shouldIncludeImportedAvails: true,
    shouldIncludeBookingSchedules: true,
  });
  const [errors, setErrors] = useState({ name: '' });
  const [isSaving, setIsSaving] = useState(false);

  /*
   * Handles the changes for the form fields.
   */
  const handleOnChange = (key, value) => {
    setCampaignData((prevData) => ({
      ...prevData,
      [key]: value,
      // toggling imported avails switch button,
      // will affect the toggle of booking schedules switch button
      ...(key === 'shouldIncludeImportedAvails' && {
        shouldIncludeBookingSchedules: value,
      }),
    }));

    if (key === 'name') {
      setCopiedCampaignName(value);
    }
  };

  /*
   * Validates the form if the required fields are populated.
   */
  const handleValidateForm = (formValues) => {
    const hasError = !formValues.name;

    if (hasError) {
      setErrors({
        name: 'Name is required',
      });
    }

    return !hasError;
  };

  /*
   * Calls an endpoint to copy and save the selected campaign.
   */
  const handleOnCopyCampaignAsync = async () => {
    if (!handleValidateForm(campaignData)) return;

    try {
      setIsSaving(true);

      await copyCampaignAsync(defaultCampaignData.id, campaignData);

      openCampaignCopiedModal();
    } catch (error) {
      console.error(error);
    } finally {
      onHide();
      setIsSaving(false);
    }
  };

  /**
   * Returns a copy campaign name prefixed with "Copy-" and truncated to a maximum of 30 characters.
   */
  const getCopyCampaignName = (campaignName) => {
    const prefix = 'Copy-';
    const maxLength = 30;

    const copyName = `${prefix}${campaignName?.trim() ?? ''}`;

    return copyName.length > maxLength
      ? copyName.substring(0, maxLength)
      : copyName;
  };

  useEffect(() => {
    const copiedName = getCopyCampaignName(defaultCampaignData.name);
    setCampaignData((prevData) => ({
      ...prevData,
      name: copiedName,
    }));
    setCopiedCampaignName(copiedName);
  }, [defaultCampaignData.name, setCopiedCampaignName]);

  return (
    <Modal
      centered
      dialogClassName={styles.copyCampaignModal}
      onHide={isSaving ? null : onHide}
      show
    >
      <Modal.Body className={styles.copyCampaignModalBody}>
        <p className={styles.modalTitle}>Copy campaign</p>

        <div className={styles.campaignNameField}>
          <OpTextBox
            errorMessage={errors.name}
            label="Campaign Name"
            maxLength="30"
            onChange={(value) => handleOnChange('name', value)}
            placeholder="Enter campaign name"
            showAsterisk
            value={campaignData.name}
          />
        </div>

        <div className={styles.switchButtons}>
          <Switch
            active={campaignData.shouldIncludeImportedAvails}
            label="Include imported avails"
            labelGap="30px"
            labelPosition="Left"
            onSwitch={(value) => handleOnChange('shouldIncludeImportedAvails', value)}
          />
          <Switch
            active={campaignData.shouldIncludeBookingSchedules}
            disabled={!campaignData.shouldIncludeImportedAvails}
            label="Include booking schedules"
            labelGap="8px"
            labelPosition="Left"
            onSwitch={(value) => handleOnChange('shouldIncludeBookingSchedules', value)}
          />
        </div>

        <div className={styles.actionButtons}>
          <OpButton label="Cancel" variant="Plain" onClick={isSaving ? null : onHide} />
          <OpButton
            label="Copy"
            loading={isSaving}
            onClick={handleOnCopyCampaignAsync}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
}

CopyCampaignModal.propTypes = {
  defaultCampaignData: PropTypes.objectOf({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  onHide: PropTypes.func.isRequired,
  openCampaignCopiedModal: PropTypes.func.isRequired,
  setCopiedCampaignName: PropTypes.func.isRequired,
};
